import { useEffect, useRef, useState, type RefObject } from "react";

interface Options {
  /** Radene i VIST rekkefølge (VIB-2528: samme sortering som tabellen). */
  items: readonly { id: string }[];
  navigate: (path: string) => void;
  /** Søkefeltet: «/» fokuserer, Esc blurrer. */
  searchRef: RefObject<HTMLInputElement>;
  /** Endres denne (f.eks. filtre) nullstilles markeringen til første rad. */
  resetKey: string;
}

/**
 * Tastatursnarveier i prosjektlisten: N (nytt), «/» (søk), piltaster og
 * Enter (åpne markert rad), Esc (ut av søkefeltet).
 *
 * Markert rad scrolles inn i bildet via desktop-raden `tr[data-project-id]`.
 * Mobilkortet bruker `data-project-card`, så vi aldri treffer et
 * display:none-element (tidligere gjorde scrollIntoView ingenting).
 */
export function useProjectListKeyboard({ items, navigate, searchRef, resetKey }: Options) {
  const [activeIdx, setActiveIdx] = useState(0);
  const lastResetKey = useRef(resetKey);

  useEffect(() => {
    if (lastResetKey.current === resetKey) return;
    lastResetKey.current = resetKey;
    setActiveIdx(0);
  }, [resetKey]);

  const activeId = items[activeIdx]?.id ?? null;

  useEffect(() => {
    if (!activeId) return;
    const el = document.querySelector<HTMLElement>(`tr[data-project-id="${activeId}"]`);
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (e.key === "Escape" && isInput && document.activeElement === searchRef.current) {
        searchRef.current?.blur();
        return;
      }
      if (isInput) return;

      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        navigate("/prosjekter/ny");
      } else if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        if (activeId) {
          e.preventDefault();
          navigate(`/prosjekter/${activeId}`);
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [items.length, activeId, navigate, searchRef]);

  return { activeIdx, activeId };
}
